import React from "react";
import { Dropdown } from "react-bootstrap";
import { FieldArrayWithId, UseFieldArrayRemove, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { PackageSearchSelectListItem } from "src/components/PackageSearchSelectListItem";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ApiError } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as EditSVG } from "go-core/images/svg/actions/edit.svg";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as MoreSVG } from "../../../../../../../../../../../images/svg/documents/more.svg";
import {
	ProductApi,
	ProductStateApi,
	StockAdjustmentApi,
	StockAdjustmentItemApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../services/Api/api";
import {
	drawProductTotalVolume,
	getProductVolumeLabel,
	isItemRowIndexShown,
	showMore,
} from "../../../../../utils/itemsTableUtils";
import {
	drawProductState,
	getInStockAmountBadgeColor,
	getStateForProduct,
} from "../../../../../utils/productStateUtils";

interface Props {
	watchedItems: StockAdjustmentItemApi[];
	fields: FieldArrayWithId<StockAdjustmentApi, "items", "key">[];
	shownIndexes: number[];
	form: UseFormReturn<StockAdjustmentApi>;
	formErrors: ApiError[];
	remove: UseFieldArrayRemove;
	fetchStockAdjustment?: () => void;
	onChangeProduct: (product: ProductApi, index: number, createdProduct?: { label: string; value: string }) => void;
	onParseProductOptionValue: (option: { label: string }) => string;
	searchProducts: (
		search: string,
		params: Record<string, any>,
		options?: Record<string, any> | undefined,
		index?: number
	) => Promise<ProductApi[]>;
	setShownIndexes: (newIndexes: number[]) => void;
	setShowChangeProductModal: (value: number | null) => void;
	productStates: ProductStateApi[];
	fetchProductLastPrice: (item: StockAdjustmentItemApi, index: number) => void;
}

const StockAdjustmentItemsTableBody = ({
	watchedItems,
	fields,
	shownIndexes,
	form,
	formErrors,
	remove,
	fetchStockAdjustment,
	onChangeProduct,
	onParseProductOptionValue,
	searchProducts,
	setShownIndexes,
	setShowChangeProductModal,
	productStates,
	fetchProductLastPrice,
}: Props) => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		formState: { errors },
		register,
		control,
		watch,
		setValue,
	} = form;
	const typeOptions: Record<string, string>[] = [
		{
			id: "INCOME",
			label: t("enums.stock_adjustments.type.INCOME"),
		},
		{
			id: "EXPENSE",
			label: t("enums.stock_adjustments.type.EXPENSE"),
		},
	];
	const organization = useSelector(selectOrganization);
	const confirmation = useConfirmation();
	const totalIncomePrice = watchedItems
		.filter((item) => item.type === "INCOME")
		.reduce((acc, cur) => acc + (cur.quantity || 0) * (cur?.price_net_money?.amount || 0), 0);
	const totalExpensePrice = watchedItems
		.filter((item) => item.type === "EXPENSE")
		.reduce((acc, cur) => acc + (cur.quantity || 0) * (cur?.product?.cost_price_money?.amount || 0), 0);
	const currency = organization?.currency ?? "PLN";
	const { isMobile } = useWindowSize();

	const handleCorrection = async (item: StockAdjustmentItemApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await api.organization().correctProductRecipe(item.product_id);
			fetchStockAdjustment && fetchStockAdjustment();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const onRemove = (index: number) => {
		remove(index);
	};

	const drawVolumeInput = (item: StockAdjustmentItemApi, index: number) => {
		const volumes = item?.product?.product_volumes || [];

		if (volumes.length > 0) {
			const volumesOptions = volumes.map((volume) => ({
				id: volume.volume,
				label: `${volume.volume ?? ""} ${volume.label ?? ""}`,
			}));
			const defaultValueLabel = volumes.find(
				(volume) => Number(volume.volume) === Number(watch(`items.${index}.volume`))
			)?.label;

			return (
				<FormSelectGroup
					name={`items.${index}.volume`}
					errors={errors}
					getOptionValue={(option) => option.id}
					defaultValue={{
						label: defaultValueLabel
							? `${watch(`items.${index}.volume`)} ${defaultValueLabel}`
							: watch(`items.${index}.volume`),
						id: watch(`items.${index}.volume`),
					}}
					loadOptions={() => volumesOptions}
					control={control}
					data-testid={`items.${index}.volume`}
					forceCreateOption
					onCreateOption={(option) => setValue(`items.${index}.volume`, option)}
				/>
			);
		}
		return (
			<FormNumberInput
				onChange={() => {
					fetchProductLastPrice(item, index);
				}}
				decimalScale={5}
				errors={errors}
				name={`items.${index}.volume`}
				suffix={
					item?.product?.measure_type ? t(`enums.products.measure_types.${item?.product?.measure_type}`) : ""
				}
				control={control}
			/>
		);
	};

	return (
		<tbody>
			{watchedItems.map((item, index) => {
				return (
					<React.Fragment key={fields[index].key}>
						<tr>
							<td className="action">
								<FormInput
									errors={errors}
									type="hidden"
									register={register}
									name={`items.${index}.position`}
									defaultValue={index}
								/>
								<FormInput
									type="hidden"
									errors={errors}
									register={register}
									name={`items.${index}.id`}
									defaultValue={item.id}
								/>
							</td>
							<td>
								<FormSelectGroup
									errors={errors}
									control={control}
									name={`items.${index}.type`}
									getOptionValue={(opt) => opt.id}
									isClearable={false}
									getOptionLabel={(opt) => opt.label}
									options={typeOptions}
									data-testid={`items.${index}.type`}
								/>
							</td>
							<td>
								{!item?.product?.id ? (
									<>
										<FormSelectGroup
											name={`items.${index}.product.name`}
											isInvalid={Boolean(
												formErrors.find((err) => err.field === `items[${index}].product_id`)
											)}
											errors={errors}
											onChange={(id, product) => onChangeProduct(product, index)}
											getOptionValue={(option) => onParseProductOptionValue(option)}
											getOptionLabel={(option) => {
												return option.product_type === "PACKAGE" ? (
													<PackageSearchSelectListItem label={option.label} />
												) : (
													option.label
												);
											}}
											defaultValue={{
												label: item?.product?.name,
												id: item?.product?.id,
											}}
											cacheUniqs={[watch(`items.${index}.type`)]}
											loadOptions={(
												search: string,
												params: Record<string, any>,
												options: Record<string, any>
											) => searchProducts(search, params, options, index)}
											control={control}
											data-testid={`items.${index}.product.name`}
										/>
										<FormInput
											type="hidden"
											errors={errors}
											register={register}
											name={`items.${index}.product_id`}
											defaultValue={item.product_id}
										/>
									</>
								) : (
									<div className="d-flex align-items-center product-box">
										<DropdownSVG
											className="icon"
											id={`icon-${index}`}
											style={{
												transform: isItemRowIndexShown(shownIndexes, index)
													? "rotate(180deg)"
													: "unset",
											}}
											onClick={() => showMore(shownIndexes, setShownIndexes, index)}
										/>
										<span className="ms-1">{`${index + 1}.`}</span>
										<Link
											rel={"noreferrer"}
											target={"_blank"}
											to={`/${organization.id}/products/${item.product.id}/edit`}
											className="mx-2"
										>
											{item.product.name}
											{getProductVolumeLabel(item.product, item)}
										</Link>
										<EditSVG
											className="me-2 icon edit-icon"
											onClick={() => setShowChangeProductModal(index)}
										/>
										<span
											className={`badge bg-${getInStockAmountBadgeColor(
												item?.product,
												productStates
											)}`}
										>
											{`${getStateForProduct(item?.product, productStates)?.in_stock_amount} ${t(
												`enums.products.measure_types.${item?.product?.measure_type}`
											)}`}
										</span>
									</div>
								)}
							</td>
							<td>{drawVolumeInput(item, index)}</td>
							<td>
								<FormNumberInput
									errors={errors}
									name={`items.${index}.quantity`}
									suffix=""
									decimalScale={5}
									defaultValue={Number(item.quantity)}
									control={control}
								/>
							</td>
							<td className="nowrap">{drawProductTotalVolume(item, t, true)}</td>
							<td colSpan={fields.length > 1 ? 1 : 2}>
								{watch(`items.${index}.type`) !== "INCOME" ? (
									<>
										<FormInput
											errors={errors}
											type="hidden"
											register={register}
											name={`items.${index}.price_net_money.amount`}
											defaultValue={item?.price_net_money?.amount || 0}
										/>
										<span>-</span>
									</>
								) : (
									<FormMoneyInput
										errors={errors}
										name={`items.${index}.price_net_money.amount`}
										withoutCurrency
										control={control}
										defaultValue={item?.price_net_money?.amount}
										currency={""}
									/>
								)}
							</td>
							{fields.length > 1 && (
								<td
									className={`${
										!((fields.length === 1 && index === 0) || index === fields.length - 1)
											? "actions"
											: "action"
									}`}
								>
									<div className="d-flex align-items-center gap-2">
										{!((fields.length === 1 && index === 0) || index === fields.length - 1) && (
											<RemoveSVG className="icon" onClick={() => onRemove(index)} />
										)}
										{item?.product_id && (
											<Dropdown className="icon">
												<Dropdown.Toggle as={MoreSVG} />
												<Dropdown.Menu>
													{item?.product_id && (
														<Dropdown.Item onClick={() => handleCorrection(item)}>
															{t("common.action.recipe_correction")}
														</Dropdown.Item>
													)}
												</Dropdown.Menu>
											</Dropdown>
										)}
									</div>
								</td>
							)}
						</tr>
						{isItemRowIndexShown(shownIndexes, index) &&
							getStateForProduct(item?.product, productStates) && (
								<tr>
									<td className="action" />
									<td />
									<td>
										<div className="d-flex flex-column">
											{item?.product?.type !== "COMPOSITE" && (
												<small className="text-muted font-weight-bold">
													{`${t("common.word.last_price")}: ${FormatMoney(
														item?.product?.last_price_money
													)}`}
												</small>
											)}
											<small className="text-muted font-weight-bold">
												{`${t("common.word.cost")}: ${FormatMoney(
													item?.product?.cost_price_money
												)}`}
											</small>
											<small className="text-muted font-weight-bold">
												{drawProductState(item.product, productStates, t)}
											</small>
											<small className="text-muted font-weight-bold">
												{`${t("common.word.default_volume")}: ${
													item?.product?.default_volume || "-"
												}`}
											</small>
										</div>
									</td>
									<td colSpan={5}>
										<FormInput
											errors={errors}
											register={register}
											className="comment-input"
											name={`items.${index}.comment`}
											defaultValue={item?.comment}
											placeholder={t("common.word.comment.placeholder")}
										/>
									</td>
								</tr>
							)}
					</React.Fragment>
				);
			})}
			<tr className="summary">
				<td className="action" />
				{!isMobile && <td colSpan={2} />}
				<td colSpan={isMobile ? 0 : 2}>{t("common.word.value")}</td>
				<td colSpan={isMobile ? 0 : 2} className={isMobile ? "text-start" : "text-end"}>
					{FormatMoney({ amount: totalIncomePrice, currency })}
				</td>
				{!isMobile && <td className="action" />}
			</tr>
			<tr className="summary">
				<td className="action" />
				{!isMobile && <td colSpan={2} />}
				<td colSpan={isMobile ? 0 : 2}>{t("common.word.estimated_cost")}</td>
				<td colSpan={isMobile ? 0 : 2} className={isMobile ? "text-start" : "text-end"}>
					{FormatMoney({
						amount: totalExpensePrice > 0 ? totalExpensePrice : totalExpensePrice * -1,
						currency,
					})}
				</td>
				{!isMobile && <td className="action" />}
			</tr>
		</tbody>
	);
};

export default StockAdjustmentItemsTableBody;
